import React, { useMemo } from 'react';
import { getScopeConfigurationDefinitions } from './scopeLib';
import { TemplateOrCalculationWithValue } from '../../../apis/api';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../store';
import { useScope } from './useScope';
import { Column, Row, Text } from '@ydistri/ds';
import ScopeConfigurationDisplay from './ScopeConfigurationDisplay';
import { ConfigurationRuleSetupType } from '@ydistri/api-sdk';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';
import {
  isModalViewMode,
  scopeConfigurationErrorStrings,
  ScopeConfigurationErrorType,
  ScopeValidationResult,
  validateScope,
  ViewMode,
} from './scopeUtilsLib';

interface ScopeConfigurationViewProps {
  scopeId: number;
  viewMode: ViewMode;
  templateOrCalculation: TemplateOrCalculationWithValue;
}

const ScopeConfigurationView: React.FC<ScopeConfigurationViewProps> = ({
  scopeId,
  viewMode,
  templateOrCalculation,
}) => {
  const scopeById = useScope(scopeId, templateOrCalculation);
  const modalScope = useSelector((state: ReduxState) => state.scope.modalScope);
  const modalChanged = useSelector((state: ReduxState) => state.scope.modalChanged);
  const isConfigurationEditable = useIsConfigurationEditable();

  const isModal = isModalViewMode(viewMode) && scopeId === modalScope?.id;

  const scope = useMemo(() => (isModal ? modalScope : scopeById), [isModal, modalScope, scopeById]);

  const scopeConfiguration = useMemo(() => scope?.configuration, [scope]);

  const configDefinitions = useMemo(
    () =>
      getScopeConfigurationDefinitions({
        scopeId,
        viewMode,
        disabled: !isConfigurationEditable,
      }),
    [scopeId, viewMode, isConfigurationEditable],
  );

  // The form's own copy while the modal edits; the server's report about the saved rule otherwise.
  const configurationValidationResult = useMemo((): ScopeValidationResult => {
    if (scopeConfiguration && scope?.entities) {
      return validateScope(scope, {
        includeConfiguration: true,
        includeScope: viewMode !== ViewMode.GENERAL_CONFIGURATION,
      });
    }
    return {
      isValid: false,
      errorType: ScopeConfigurationErrorType.ERROR,
      errorMessage: scopeConfigurationErrorStrings[ScopeConfigurationErrorType.ERROR],
    };
  }, [scopeConfiguration, scope, viewMode]);
  if (!scopeConfiguration) {
    return null;
  }

  // In the modal the local check speaks while the user edits; on the card the server's problems
  // are written at the card's foot (ExceptionDefinition), so nothing is said twice here.
  const errorText =
    isModal && modalChanged && !configurationValidationResult.isValid
      ? configurationValidationResult.errorMessage
      : '';
  const displayError = errorText !== '' && viewMode !== ViewMode.GENERAL_CONFIGURATION;

  return (
    <Row $gap="0.5rem" $justifyContent="space-between">
      <Column $gap="0.5rem">
        <ScopeConfigurationDisplay
          definition={configDefinitions[ConfigurationRuleSetupType.UseAsSource]}
          scopeConfiguration={scopeConfiguration}
        />
        <ScopeConfigurationDisplay
          definition={configDefinitions[ConfigurationRuleSetupType.UseAsTarget]}
          scopeConfiguration={scopeConfiguration}
        />
        <ScopeConfigurationDisplay
          definition={configDefinitions[ConfigurationRuleSetupType.IsEnabled]}
          scopeConfiguration={scopeConfiguration}
        />
      </Column>
      <Column $gap="0.5rem" $alignItems="end">
        <ScopeConfigurationDisplay
          definition={
            configDefinitions[ConfigurationRuleSetupType.MaxNumberOfSourcePickingPositions]
          }
          scopeConfiguration={scopeConfiguration}
        />
        <ScopeConfigurationDisplay
          definition={configDefinitions[ConfigurationRuleSetupType.MaxNumberOfTargetStores]}
          scopeConfiguration={scopeConfiguration}
        />
        <ScopeConfigurationDisplay
          definition={configDefinitions[ConfigurationRuleSetupType.MaxNumberOfSourceStores]}
          scopeConfiguration={scopeConfiguration}
        />
        <ScopeConfigurationDisplay
          definition={configDefinitions[ConfigurationRuleSetupType.MinimumRouteValue]}
          scopeConfiguration={scopeConfiguration}
        />
        {displayError && <Text $type="danger">{errorText}</Text>}
      </Column>
    </Row>
  );
};

export default ScopeConfigurationView;
